import { type FC, type PropsWithChildren } from "react";

import { Button } from "@/components/flexkit/Button";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { cn } from "@/components/podkit/lib/cn";
import { SecretLabels } from "@/components/secrets/secret-labels";
import { SecretsTable } from "@/components/secrets/SecretsTable";
import { type PlainSecret } from "@/queries/secret-queries";
import { type SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";

type Props = {
    scope: SecretScope;
    secrets: PlainSecret[];
    onNewSecret: () => void;
    showCreatorColumn?: boolean;
    canCreateSecret?: boolean;
    canUpdateSecret?: boolean;
    canDeleteSecret?: boolean;
};
export const SecretsCard: FC<Props> = ({
    scope,
    secrets,
    showCreatorColumn = true,
    onNewSecret,
    canCreateSecret = true,
    canUpdateSecret = true,
    canDeleteSecret = true,
}) => {
    // Filter out API-only secrets from the UI
    // apiOnly is part of the mount oneof, so check if mount.case is "apiOnly"
    const userVisibleSecrets = secrets.filter((secret) => secret.mount?.case !== "apiOnly");

    if (userVisibleSecrets.length === 0) {
        return <SecretsEmptyState scope={scope} onNewSecret={onNewSecret} canCreateSecret={canCreateSecret} />;
    }

    return (
        <SecretsTable
            secrets={userVisibleSecrets}
            showCreatorColumn={showCreatorColumn}
            canUpdateSecret={canUpdateSecret}
            canDeleteSecret={canDeleteSecret}
        />
    );
};

// this is reused for showing secrets in the environment details
export const SecretsFrame: FC<PropsWithChildren> = ({ children }) => {
    return (
        <div className="w-full rounded-xl border border-border-base bg-surface-glass px-2 py-3 sm:px-4">{children}</div>
    );
};

const SecretsEmptyState: FC<{
    scope: SecretScope;
    onNewSecret: () => void;
    canCreateSecret: boolean;
}> = ({ scope, onNewSecret, canCreateSecret }) => {
    const mode = scope.scope.case === "projectId" ? "project" : "user";
    return (
        <EmptyStateCard
            title="No secrets"
            description={SecretLabels[mode].emptyStateDescription}
            data-testid="secrets-empty-state"
        >
            <Button
                onClick={onNewSecret}
                data-testid="add-secret-modal-trigger"
                data-tracking-id="open-new-secret-modal-secrets-card"
                className={cn({ hidden: !canCreateSecret })}
            >
                New Secret
            </Button>
        </EmptyStateCard>
    );
};
